import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import type { ColumnMetaInfo } from '../../services/data-integration-api';
import type { ProdColumnInfo, ProdColumnBinding } from '../../services/data-integration-api';

interface ProdColumnBindingEditorProps {
  sourceColumns: Array<ColumnMetaInfo & { column_key_type?: string | null }>;
  prodColumns: ProdColumnInfo[];
  initialBindings?: ProdColumnBinding[];
  onChange: (bindings: ProdColumnBinding[]) => void;
}

/** 简单的类型兼容判断 */
function isTypeCompatible(sourceType: string, prodType: string): boolean {
  const normalize = (t: string) => {
    const lower = t.toLowerCase();
    if (/int|serial|bigint|smallint|tinyint|mediumint/.test(lower)) return 'integer';
    if (/varchar|char|text|longtext|mediumtext/.test(lower)) return 'string';
    if (/decimal|numeric|float|double|real/.test(lower)) return 'number';
    if (/date|time|datetime|timestamp/.test(lower)) return 'datetime';
    if (/bool/.test(lower)) return 'boolean';
    return lower;
  };
  return normalize(sourceType) === normalize(prodType);
}

export default function ProdColumnBindingEditor({
  sourceColumns, prodColumns, initialBindings, onChange,
}: ProdColumnBindingEditorProps) {
  const [bindings, setBindings] = useState<ProdColumnBinding[]>([]);

  // 初始化绑定
  useEffect(() => {
    if (initialBindings && initialBindings.length > 0) {
      setBindings(initialBindings);
      return;
    }
    // 自动匹配同名字段
    const auto: ProdColumnBinding[] = sourceColumns.map(sc => {
      const match = prodColumns.find(pc => pc.column_name === sc.column_name);
      if (match) {
        const mismatch = !isTypeCompatible(sc.data_type, match.data_type);
        return {
          source_column_name: sc.column_name,
          source_data_type: sc.data_type,
          prod_column_name: match.column_name,
          prod_data_type: match.data_type,
          type_mismatch: mismatch,
          transform_rule: mismatch ? 'type_cast' as const : 'direct' as const,
          transform_expression: null,
          is_dict_field: false,
          is_fk_field: false,
        };
      }
      return {
        source_column_name: sc.column_name,
        source_data_type: sc.data_type,
        prod_column_name: '',
        prod_data_type: '',
        type_mismatch: false,
        transform_rule: 'direct' as const,
        transform_expression: null,
        is_dict_field: false,
        is_fk_field: false,
      };
    });
    setBindings(auto);
  }, [sourceColumns, prodColumns, initialBindings]);

  const updateBinding = useCallback((index: number, updates: Partial<ProdColumnBinding>) => {
    setBindings(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      // 自动检测类型不匹配
      if (updates.prod_column_name !== undefined) {
        const prodCol = prodColumns.find(pc => pc.column_name === updates.prod_column_name);
        if (prodCol) {
          next[index].prod_data_type = prodCol.data_type;
          const mismatch = !isTypeCompatible(next[index].source_data_type, prodCol.data_type);
          next[index].type_mismatch = mismatch;
          if (mismatch && next[index].transform_rule === 'direct') {
            next[index].transform_rule = 'type_cast';
          }
        } else {
          next[index].prod_data_type = '';
          next[index].type_mismatch = false;
        }
      }
      onChange(next);
      return next;
    });
  }, [prodColumns, onChange]);

  // 已被绑定的生产库字段
  const boundProdCols = new Set(bindings.map(b => b.prod_column_name).filter(Boolean));
  const unboundCount = bindings.filter(b => !b.prod_column_name).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium">字段绑定 ({bindings.length} 个源字段)</h4>
        {unboundCount > 0 && (
          <Badge variant="secondary">{unboundCount} 个未绑定</Badge>
        )}
      </div>

      <div className="border rounded-md overflow-auto max-h-[500px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">源字段</TableHead>
              <TableHead className="w-[100px]">源类型</TableHead>
              <TableHead className="w-[40px]">→</TableHead>
              <TableHead className="w-[180px]">生产库字段</TableHead>
              <TableHead className="w-[100px]">生产库类型</TableHead>
              <TableHead className="w-[100px]">转换规则</TableHead>
              <TableHead className="w-[70px]">字典字段</TableHead>
              <TableHead className="w-[70px]">FK 字段</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bindings.map((b, i) => (
              <TableRow key={b.source_column_name} className={b.type_mismatch ? 'bg-yellow-50' : ''}>
                <TableCell className="font-mono text-xs">{b.source_column_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{b.source_data_type}</TableCell>
                <TableCell className="text-center text-muted-foreground">→</TableCell>
                <TableCell>
                  <select
                    value={b.prod_column_name}
                    onChange={e => updateBinding(i, { prod_column_name: e.target.value })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                  >
                    <option value="">-- 未绑定 --</option>
                    {prodColumns.map(pc => (
                      <option
                        key={pc.column_name}
                        value={pc.column_name}
                        disabled={boundProdCols.has(pc.column_name) && b.prod_column_name !== pc.column_name}
                      >
                        {pc.column_name}{pc.column_comment ? ` (${pc.column_comment})` : ''}
                      </option>
                    ))}
                  </select>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {b.prod_data_type}
                  {b.type_mismatch && <Badge variant="destructive" className="ml-1 text-[10px]">类型不匹配</Badge>}
                </TableCell>
                <TableCell>
                  {b.prod_column_name && (
                    <select
                      value={b.transform_rule}
                      onChange={e => updateBinding(i, { transform_rule: e.target.value as ProdColumnBinding['transform_rule'] })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                    >
                      <option value="direct">直接映射</option>
                      <option value="type_cast">类型转换</option>
                      <option value="custom">自定义</option>
                    </select>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {b.prod_column_name && (
                    <Checkbox
                      checked={b.is_dict_field}
                      disabled={b.is_fk_field}
                      onCheckedChange={checked => updateBinding(i, { is_dict_field: checked === true, is_fk_field: false })}
                    />
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {b.prod_column_name && (
                    <Checkbox
                      checked={b.is_fk_field}
                      disabled={b.is_dict_field}
                      onCheckedChange={checked => updateBinding(i, { is_fk_field: checked === true, is_dict_field: false })}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
